import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export const ACTIVITY_KEY = "rs_last_activity";
const LOGOUT_KEY = "rs_logout";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  useEffect(() => {
    let refreshing = null;
    const id = api.interceptors.response.use(undefined, async (error) => {
      const cfg = error.config;
      if (error.response?.status !== 401 || !cfg || cfg._retried || cfg.url?.startsWith("/auth/")) {
        return Promise.reject(error);
      }
      cfg._retried = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        return api(cfg);
      } catch {
        setUser(false);
        return Promise.reject(error);
      } finally {
        refreshing = null;
      }
    });
    return () => { api.interceptors.response.eject(id); };
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LOGOUT_KEY || !e.newValue) return;
      if (e.newValue.startsWith("expired")) sessionStorage.setItem("rs_session_expired", "1");
      setUser(false);
    };
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("storage", onStorage); };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    setUser(data);
    return data;
  };

  const logout = async (reason = "manual") => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.setItem(LOGOUT_KEY, `${reason}:${Date.now()}`);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, reload: loadUser, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
